import {useEffect, useMemo} from 'react';
import {useAtom, useAtomValue} from 'jotai';
import {useLocation} from 'react-router-dom';
import {X, Send, ChevronDown, CornerDownLeft, RotateCcw} from 'lucide-react';
import {
  aiPanelOpenAtom,
  aiPushModeAtom,
  aiLoadingAtom,
  resolvedQueueIdsAtom,
  aiMessagesAtom,
  aiHasUnreadReplyAtom,
  processedRecordsAtom,
  type AiMessage,
} from '@lib/atoms';
import {DASH_QUEUE_URGENT} from '@pages/dashboard-page/model/mock';
import {MOCK_TASKS} from '@api/tasks/mock';
import {MOCK_COMMUNICATIONS} from '@api/meerec/mock';
import {MOCK_MEETINGS} from '@api/meetings/mock';
import {MOCK_TODAY} from '@lib/utils';
import {useWorkspaceModules} from '@api/workspaces';
import {ModalPresence} from '@ui/local/ModalPresence';
import {SparkleAi} from '@ui/primitives/SparkleAi';
import {Button} from '@ui/shadcn/button';
import {Textarea} from '@ui/local/Textarea';
import {useState} from 'react';

// Quick-prompts с предзаданными ответами (раунд 10 §3.3).
const QUICK_PROMPTS: {q: string; a: string}[] = [
  {
    q: 'Почему упали продажи на этой неделе?',
    a: 'Конверсия упала на 4% (с 62% до 58%). Основной фактор — Сергей М. (с 72% → 54%) и Дмитрий Р. (с 65% → 52%). Возражение «дорого» отрабатывается в 38% случаев (норма ≥50%). Рекомендую тренинг по работе с ценой и индивидуальный разбор с этими двумя.',
  },
  {
    q: 'Кому из менеджеров нужна помощь?',
    a: 'Внимания требуют: Иван С. — средняя оценка 5.8/10, низкое соблюдение скрипта (62%). Дмитрий Р. — конверсия упала на 18 пп. Сергей М. — затяжные звонки без следующего шага в 4 случаях за неделю. План — назначить 1-на-1, прослушать по 3 звонка с каждым.',
  },
  {
    q: 'Какие темы обучения актуальны?',
    a: 'Топ-3 пробела: 1) «Дорого, у конкурента дешевле» — 8 проваленных звонков за неделю, скрипта нет. 2) Закрытие на следующий шаг — у 60% звонков нет CTA. 3) Cross-sell — не предложен в 90% подходящих кейсов. Подготовлю расчёт на учебный модуль.',
  },
  {
    q: 'Сравни с прошлым месяцем',
    a: 'Сравнение к апрелю: звонков +12% (1840 vs 1640), средняя оценка стабильна 7.0 vs 7.1, конверсия −3 пп (58% vs 61%), доля сделок с фиксацией +5 пп. Сильнее всего просел этап работы с возражениями. Положительная динамика — выявление потребностей.',
  },
];

type PageKey =
  | 'dashboard'
  | 'calls'
  | 'meetings'
  | 'tasks'
  | 'processed'
  | 'other';

const pathToPage = (pathname: string): PageKey => {
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/calls')) return 'calls';
  if (pathname.startsWith('/meetings')) return 'meetings';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/processed')) return 'processed';
  return 'other';
};

const PAGE_LABEL: Record<PageKey, string> = {
  dashboard: 'дашборд',
  calls: 'звонки',
  meetings: 'встречи',
  tasks: 'задачи',
  processed: 'обработанные',
  other: '',
};

/** Склонение «звонок / звонка / звонков». */
const pluralCalls = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'звонок';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'звонка';
  return 'звонков';
};

/** Склонение «задача / задачи / задач». */
const pluralTasks = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'задача';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'задачи';
  return 'задач';
};

/** Склонение «запись / записи / записей». */
const pluralRecords = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'запись';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'записи';
  return 'записей';
};

/** Склонение «встреча / встречи / встреч». */
const pluralMeetings = (n: number): string => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'встреча';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'встречи';
  return 'встреч';
};

const FALLBACK_REPLY =
  'Спасибо за вопрос! Я ещё учусь работать с произвольными запросами. Попробуйте одну из подсказок ниже.';

const getAnswer = (text: string): string => {
  const match = QUICK_PROMPTS.find((p) => p.q === text);
  return match ? match.a : FALLBACK_REPLY;
};

// ─── Main panel ─────────────────────────────────────────────────────────────

/** Таймер «генерации ответа» на уровне модуля (а не useRef): переживает
 *  размонтирование панели при сворачивании (collapse) — мышление продолжается. */
let pendingReplyTimer: ReturnType<typeof setTimeout> | null = null;

/** Правая AI-панель «Нейроаналитик» (раунд 10 §3, обновл. раунд 29). */
export const AiPanel = ({motionKey}: {motionKey?: string}) => {
  const [open, setOpen] = useAtom(aiPanelOpenAtom);
  const pushMode = useAtomValue(aiPushModeAtom);
  const resolvedIds = useAtomValue(resolvedQueueIdsAtom);
  const location = useLocation();

  // Сообщения, loading и unread-флаг живут в jotai — переживают сворачивание
  // (collapse) и повторное открытие панели (но не reload страницы).
  const [messages, setMessages] = useAtom(aiMessagesAtom);
  const [hasUnreadReply, setHasUnreadReply] = useAtom(aiHasUnreadReplyAtom);
  const [loading, setLoading] = useAtom(aiLoadingAtom);

  const [input, setInput] = useState('');

  // Пока дровер открыт — «непрочитанных» нет (их показывает только свёрнутая
  // кнопка «Нейроаналитик»). Сбрасываем флаг, когда панель открыта и не грузится.
  useEffect(() => {
    if (open && !loading) setHasUnreadReply(false);
  }, [open, loading, messages.length, setHasUnreadReply]);

  const page = useMemo(
    () => pathToPage(location.pathname),
    [location.pathname],
  );
  // На `/meetings` с выключенным модулем висит заглушка «ещё не подключён» —
  // встреч там нет, поэтому приветствие про них заменяем на общее. Сам ключ
  // страницы не подменяем: он подписывает контекст панели («встречи»).
  const {meetings: meetingsEnabled} = useWorkspaceModules();
  const processed = useAtomValue(processedRecordsAtom);

  /** Сколько звонков в «Требуют внимания» сейчас — для дашбордового приветствия. */
  const attentionCount = useMemo(
    () => DASH_QUEUE_URGENT.filter((q) => !resolvedIds.has(q.id)).length,
    [resolvedIds],
  );

  /** Количество звонков за сегодня (для страницы «Звонки»). */
  const callsToday = useMemo(
    () =>
      MOCK_COMMUNICATIONS.filter((c) => c.dateTime.startsWith(MOCK_TODAY))
        .length,
    [],
  );

  /** Количество встреч за сегодня (для страницы «Встречи»). */
  const meetingsToday = useMemo(
    () =>
      MOCK_MEETINGS.filter((m) => m.occurredAt.startsWith(MOCK_TODAY)).length,
    [],
  );

  /** Открытые / просроченные задачи (для страницы «Задачи»). */
  const taskStats = useMemo(() => {
    const open = MOCK_TASKS.filter((t) => t.status !== 'done').length;
    const overdue = MOCK_TASKS.filter(
      (t) => t.status !== 'done' && t.dueDate < MOCK_TODAY,
    ).length;
    return {open, overdue};
  }, []);

  /** Динамическое приветствие для текущей страницы. */
  const greeting = useMemo(() => {
    const head = 'Привет! Я Виртуальный РОП';
    switch (page) {
      case 'dashboard':
        return `${head}, помогу проанализировать звонки команды. Вижу, у вас ${attentionCount} ${pluralCalls(attentionCount)} требуют внимания. С чего начнём?`;
      case 'calls':
        return `${head}. На странице звонков вижу ${callsToday} ${pluralCalls(callsToday)} за сегодня — могу подсветить аномалии, разобрать причины отказов или сравнить менеджеров. С чего начнём?`;
      case 'meetings':
        // Модуль выключен — на странице заглушка, встреч нет: общее приветствие.
        return meetingsEnabled
          ? `${head}. На странице встреч вижу ${meetingsToday} ${pluralMeetings(meetingsToday)} за сегодня — могу разобрать конверсию по очным/онлайн, найти слабые места в подготовке или сравнить менеджеров. С чего начнём?`
          : `${head}, помогу проанализировать звонки команды. С чего начнём?`;
      case 'tasks':
        return `${head}. У вас ${taskStats.open} открытых ${pluralTasks(taskStats.open)}${taskStats.overdue > 0 ? `, из них ${taskStats.overdue} просрочено` : ''}. Помогу расставить приоритеты или связать задачи со звонками. С чего начнём?`;
      case 'processed':
        return `${head}. В обработанных за период ${processed.length} ${pluralRecords(processed.length)}. Могу подсветить лучшие практики или составить выжимку. С чего начнём?`;
      default:
        return `${head}, помогу проанализировать звонки команды. С чего начнём?`;
    }
  }, [
    page,
    attentionCount,
    callsToday,
    meetingsToday,
    meetingsEnabled,
    taskStats,
    processed.length,
  ]);

  const sendMessage = (text: string) => {
    const t = text.trim();
    if (!t || loading) return;
    setMessages((prev: AiMessage[]) => [...prev, {role: 'user', content: t}]);
    setInput('');
    setLoading(true);
    if (pendingReplyTimer) clearTimeout(pendingReplyTimer);
    // Таймер на уровне модуля — ответ догенерируется, даже если панель свернули.
    pendingReplyTimer = setTimeout(
      () => {
        setMessages((prev: AiMessage[]) => [
          ...prev,
          {role: 'assistant', content: getAnswer(t)},
        ]);
        setLoading(false);
        setHasUnreadReply(true);
        pendingReplyTimer = null;
      },
      600 + Math.random() * 400,
    );
  };

  /** Прервать текущую генерацию ответа (для закрытия и рестарта). */
  const abortReply = () => {
    if (pendingReplyTimer) {
      clearTimeout(pendingReplyTimer);
      pendingReplyTimer = null;
    }
    setLoading(false);
  };

  const handleRestart = () => {
    abortReply();
    setMessages([]);
    setHasUnreadReply(false);
    setInput('');
  };

  // Свернуть: прячем дровер (возвращается кнопка «Нейроаналитик»), НЕ прерывая
  // генерацию — ответ догенерируется в фоне (ТЗ §7).
  const handleCollapse = () => {
    setOpen(false);
  };

  // Закрыть: останавливаем процесс мышления/выдачи ответа и прячем дровер (ТЗ §7).
  const handleClose = () => {
    abortReply();
    setHasUnreadReply(false);
    setOpen(false);
  };

  // Закрыта/свёрнута — дровер не рендерим (на экране кнопка «Нейроаналитик»).
  const renderPanel = () => {
    if (!open) return null;

    const hasConversation = messages.length > 0;

    return (
      <>
        {/* Overlay — только в overlay-режиме (на узких экранах). В push-режиме
          контент уже сжался, затемнение не нужно. */}
        {!pushMode && (
          <div
            className='modal-overlay fixed inset-0 bg-black/15'
            style={{zIndex: 'calc(var(--z-ai-panel) - 5)'}}
            onClick={handleClose}
          />
        )}

        {/* Panel (right) */}
        <div
          className='modal-panel-right fixed top-0 right-0 bottom-0 w-[400px] max-w-[90vw] bg-[var(--color-surface)] border-l border-[var(--color-border)] flex flex-col shadow-2xl'
          style={{zIndex: 'var(--z-ai-panel)'}}
        >
          {/* Header */}
          <div
            className='flex items-center justify-between gap-3 px-4 shrink-0 border-b border-[var(--color-border)] bg-white'
            style={{height: 68}}
          >
            <div className='flex items-center gap-3 min-w-0'>
              <span
                className='flex items-center justify-center shrink-0 text-white'
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: 'var(--color-primary)',
                }}
              >
                <SparkleAi size={22} />
              </span>
              <div className='min-w-0 leading-tight'>
                <p className='text-base font-semibold text-[var(--color-foreground)] truncate'>
                  Нейроаналитик
                </p>
                <p className='text-xs text-[var(--color-muted-foreground)] truncate mt-0.5'>
                  Анализирует команду А
                </p>
              </div>
            </div>
            <div className='flex items-center gap-1.5 shrink-0'>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                onClick={handleCollapse}
                title='Свернуть'
                aria-label='Свернуть'
                className='text-[var(--color-secondary)] hover:text-[var(--color-foreground)]'
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: '#f3f4f6',
                }}
              >
                <ChevronDown size={16} />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                onClick={handleClose}
                title='Закрыть'
                aria-label='Закрыть'
                className='text-[var(--color-secondary)] hover:text-[var(--color-foreground)]'
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  backgroundColor: '#f3f4f6',
                }}
              >
                <X size={16} />
              </Button>
            </div>
          </div>

          {/* Restart bar — появляется после первой отправки */}
          {hasConversation && (
            <div className='px-3 py-2 border-b border-[var(--color-border)] shrink-0 flex justify-end'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleRestart}
                className='text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)]'
                title='Очистить переписку и начать заново'
              >
                <RotateCcw size={13} />
                Начать заново
              </Button>
            </div>
          )}

          {/* Messages */}
          <div className='flex-1 overflow-y-auto px-3 py-3 space-y-3'>
            {/* Greeting — без аватарки, только серый bubble */}
            <div className='flex justify-start'>
              <div className='max-w-[92%] bg-[var(--color-muted)] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed'>
                {greeting}
              </div>
            </div>

            {/* Messages */}
            {messages.map((msg, i) =>
              msg.role === 'user' ? (
                <div key={i} className='flex justify-end'>
                  <div className='max-w-[88%] text-sm rounded-2xl rounded-br-sm bg-[var(--color-primary)] text-white px-3.5 py-2.5 leading-relaxed'>
                    {msg.content}
                  </div>
                </div>
              ) : (
                <div key={i} className='flex gap-2'>
                  <span className='w-7 h-7 rounded-full bg-[var(--color-primary)] text-white flex items-center justify-center shrink-0'>
                    <SparkleAi size={15} />
                  </span>
                  <div className='max-w-[88%] bg-[var(--color-muted)] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed'>
                    {msg.content}
                  </div>
                </div>
              ),
            )}

            {loading && (
              <div className='flex gap-2'>
                <span className='w-7 h-7 rounded-full bg-[var(--color-primary)] text-white flex items-center justify-center shrink-0'>
                  <SparkleAi size={15} />
                </span>
                <div className='bg-[var(--color-muted)] rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5 items-center'>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className='w-1.5 h-1.5 rounded-full bg-[var(--color-secondary)] animate-bounce'
                      style={{animationDelay: `${i * 150}ms`}}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Quick prompts */}
            <div className='space-y-2 pt-2'>
              <p className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]'>
                Подсказки
              </p>
              <div className='flex flex-col gap-1.5'>
                {QUICK_PROMPTS.map((p) => (
                  <Button
                    key={p.q}
                    type='button'
                    variant='outline'
                    onClick={() => sendMessage(p.q)}
                    disabled={loading}
                    className='h-auto justify-start whitespace-normal text-left text-sm py-2 rounded-lg font-normal leading-snug hover:bg-[var(--color-primary-soft)] hover:border-[var(--color-primary)]'
                  >
                    {p.q}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Input */}
          <div className='px-3 py-3 border-t border-[var(--color-border)] shrink-0'>
            <div className='flex gap-2 items-end'>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder='Спросите Нейроаналитика...'
                rows={1}
                className='flex-1 resize-none rounded-xl leading-relaxed max-h-24'
              />
              <Button
                type='button'
                size='icon'
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                className='rounded-xl'
                aria-label='Отправить'
              >
                <Send size={14} />
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className='px-4 py-2 border-t border-[var(--color-border)] shrink-0 flex items-center justify-between gap-2'>
            <p className='text-[11px] text-[var(--color-muted-foreground)] truncate'>
              Контекст: команда A · {PAGE_LABEL[page] || page}
            </p>
            <span className='inline-flex items-center gap-1 text-[11px] text-[var(--color-muted-foreground)] shrink-0'>
              <CornerDownLeft size={11} /> отправить
            </span>
          </div>
        </div>
      </>
    );
  };
  return <ModalPresence key={motionKey}>{renderPanel()}</ModalPresence>;
};
